/* eslint-disable import/max-dependencies */
// @flow
import React, { type StatelessFunctionalComponent, type Element } from "react";
import { useDispatch } from "react-redux";
import { useTable, type TableProps } from "@fas/ui-framework/lib/services/table";
import {
  Table,
  IsActiveLabel,
  ViewLink,
  SelectSearchComponent,
  ShowMoreItems,
} from "@fas/ui-core";
import type { Column } from "@fas/ui-core/lib/Table/Table.types";
import { Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import type { SearchComponentProps } from "@fas/ui-core/lib/SearchComponents";
import { getPWAListSaga } from "../../actions/pwaList";
import externalRedirect from "../../components/App/ExternalRedirect";
import { PWA_LIST_TABLE } from "../../helpers/constants";
import { getFields, compareById } from "../../services/utils";
import type { Classes, PwaListItem } from "./types/PwaMotivationContentList.types";
import MotivationTemplatesColumn from "../../components/MotivationTemplatesColumn";

type Props = $ReadOnly<{|
  Actions?: Element<*>,
  choicedData?: Array<PwaListItem>,
  onSelect?: (PwaListItem) => mixed,
  onDeselect?: (PwaListItem) => mixed,
  onCreate?: () => mixed,
  allSelectAvailable?: () => boolean,
  rowSelectAvailable?: () => boolean,
|}>;

const useStyles: () => Classes = makeStyles((): {[$Keys<Classes>]: *} => ({
  fixedSizeColumn: {
    width: "20%",
  },
}));

const handleCreate: () => null = () => externalRedirect({ to: "/pwa/create", target: "_self" });

const PwaMotivationContentList: StatelessFunctionalComponent<Props> = ({
  Actions,
  choicedData,
  onSelect,
  onDeselect,
  onCreate = handleCreate,
  allSelectAvailable = (): boolean => true,
  rowSelectAvailable = (): boolean => true,
}) => {
  const classes: Classes = useStyles();
  const tableProps: TableProps = useTable(PWA_LIST_TABLE);
  const dispatch: <A>(A) => A = useDispatch();

  const columns: Column<PwaListItem>[] = [
    {
      label: "Status",
      field: "isActive",
      searchable: true,
      SearchComponent: (props: SearchComponentProps): Element<*> => (
        <SelectSearchComponent
          {...props}
          label="Select"
          values={[
            { value: "", title: "None" },
            { value: "true", title: "Active" },
            { value: "false", title: "Inactive" },
          ]}
        />
      ),
      render: IsActiveLabel,
    },
    {
      label: "Id",
      field: "id",
    },
    {
      label: "Name",
      field: "name",
      searchable: true,
      render: (row: PwaListItem): Element<*> => <ViewLink {...row} viewPath="/pwa/view/:id" />,
    },
    {
      label: "Behavior To Action",
      field: "behaviorToAction",
    },
    {
      field: "motivationTemplates",
      label: "Motivation Templates",
      className: classes.fixedSizeColumn,
      render: MotivationTemplatesColumn,
    },
    {
      field: "BrowserLanguages",
      label: "Browser Languages",
      searchable: true,
      className: classes.fixedSizeColumn,
      render: (row: PwaListItem): Element<*> => <ShowMoreItems data={getFields(row.BrowserLanguages, "language")} />,
    },
  ];

  const onLoad: () => mixed = () => dispatch(getPWAListSaga());

  return (
    <Box m={2}>
      <Table
        {...tableProps}
        columns={columns}
        title="PWA Motivation Content"
        onLoad={onLoad}
        onCreate={onCreate}
        selected={choicedData || tableProps.selected}
        onSelect={onSelect || tableProps.onSelect}
        onDeselect={onDeselect || tableProps.onDeselect}
        rowSelectAvailable={rowSelectAvailable}
        allSelectAvailable={allSelectAvailable}
        compareRows={compareById}
        Actions={Actions}
      />
    </Box>
  );
};

export default PwaMotivationContentList;
